import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import AddIcon from "@mui/icons-material/Add";
import MoreVert from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "./utils/tableUtils.js";
import BankAccountKundeModal from "../modals/BankAccountKundeModal.jsx";
import SetDefaultAccountDialog from "../modals/SetDefaultAccountDialog.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useBankAccountActions } from "hooks/useBankAccountActions.jsx";
import BulkAssignObjectsDialog from "components/modals/BulkAssignObjectsDialog.jsx";
import AddBankAccountDialog from "components/modals/AddBankAccountDialog.jsx";
import AssignObjectsChoiceDialog from "pages/admin/customers/components/AssignObjectsChoiceDialog.jsx";
import BankAccountObjectImportDialog from "pages/admin/customers/components/BankAccountObjectImportDialog.jsx";
import EditBankAccountDialog from "pages/admin/customers/components/EditBankAccountDialog.jsx";
import { Button } from "@accredo/fe-shared/theme/components";

const REVIEW_STATE_COLOR = {
  pending: "warning",
  claims_relevant: "success",
  rejected: "default",
};

/**
 * Bankkonten - eine Tabelle für beide Kontexte, unterschieden nur durch
 * `scope`:
 * - "admin": /admin/bankaccounts, sysadmin, kundenübergreifend (Kunde-Spalte,
 *   kunde_mismatch-Warnung, Kunde-ändern-Dialog).
 * - "customer": Bankkonten-Tab der Kundendetailseite und die
 *   Kunden-Selbstverwaltung (/bankkonten), auf `customerId` beschränkt.
 *
 * Jede Aktion ist ausschließlich über die vom Backend berechneten can_*-Flags
 * gegattert (BankAccountResource::permissionFlags(), nach der Policy-Korrektur
 * korrekt kundenscoped) - beide scopes zeigen also automatisch nur, was die
 * jeweilige Rolle tatsächlich darf.
 */
export default function BankAccountTable({ scope = "admin", customerId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [kundeModalBankAccount, setKundeModalBankAccount] = useState(null);
  const [editDialogAccount, setEditDialogAccount] = useState(null);
  const [choiceDialogAccount, setChoiceDialogAccount] = useState(null);
  const [bulkDialogAccount, setBulkDialogAccount] = useState(null);
  const [importDialogAccount, setImportDialogAccount] = useState(null);
  const [defaultDialogAccount, setDefaultDialogAccount] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const { user } = useUser();
  const {
    setReviewState,
    setCustomerDefault,
    detachObject,
    loadReviewStateCounts,
  } = useBankAccountActions();

  // Mirrors BankAccountPolicy::canWrite() — write access is "all" (accredo/
  // sysadmin) or "customer" (kd_verwaltung), never "own" (field staff).
  const canCreateAccount = user?.hasPermission?.([
    "vertrag_objekt_all",
    "vertrag_objekt_customer",
  ]);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [reviewStateCounts, setReviewStateCounts] = useState(null);

  // The review state lives in the URL so the "new accounts imported"
  // notification can deep-link straight into the pending accounts. Merged
  // into the existing params rather than replacing them outright, since the
  // customer scope renders inside a page (CustomerShowPage) that may carry
  // its own search params.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeReviewState = searchParams.get("review_state") || "all";

  const tableKey = scope === "admin" ? "bank_accounts" : "customerBankAccounts";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Bankkonten",
        route:
          scope === "admin"
            ? "/data/bank-accounts"
            : `/data/kundes/${customerId}/bank-accounts`,
        pageSize: 10,
        storageKey:
          scope === "admin"
            ? "v2_table_bank_accounts"
            : "v2_table_customer_bank_accounts",
        extraParams: {
          include:
            scope === "admin"
              ? ["objekte", "kunde", "objekte.vertrag.kunde"]
              : ["objekte", "kunde"],
          // Standard query-builder filter, resolved in BankAccountRepository.
          ...(activeReviewState !== "all" && {
            filters: { review_state: activeReviewState },
          }),
        },
      },
    ],
    [scope, customerId, activeReviewState, tableKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];
  const totalRows = tableState.totalRows ?? rows.length;
  const pageIndex = tableState.pageIndex ?? 0;
  const pageSize = tableState.pageSize ?? 10;
  const isLoading = tableState.isLoading ?? false;
  const hasLoaded = tableState.hasLoaded ?? false;

  // Initial load and every tab change go through the hook's own fetch path.
  // shouldFetch must be passed explicitly: updateTableState skips the update
  // when no value actually changes, and pageIndex is already 0, so
  // { pageIndex: 0 } alone is a no-op that never triggers a fetch.
  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    loadReviewStateCounts(setReviewStateCounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReviewState, customerId]);

  /** Keep the review state in the URL so the notification can deep-link to it. */
  const handleTabChange = (value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("review_state");
        } else {
          next.set("review_state", value);
        }
        return next;
      },
      { replace: true }
    );
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  /** Re-read the page so review state, default marker and links stay in sync. */
  const refresh = () => {
    fetchData(tableKey);
    loadReviewStateCounts(setReviewStateCounts);
  };

  const runAction = async (action) => {
    handleMenuClose();
    await action();
    refresh();
  };

  const handleAssignChoiceContinue = (method) => {
    const bank_account = choiceDialogAccount;
    setChoiceDialogAccount(null);
    if (method === "excel") {
      setImportDialogAccount(bank_account);
    } else {
      setBulkDialogAccount(bank_account);
    }
  };

  // Spalten-Definition
  const columns = useMemo(
    () => [
      {
        accessorKey: "iban_bic",
        accessorKeys: ["iban", "bic"],
        header: "IBAN/BIC",
        size: 200,
        Cell: ({ row }) => {
          const iban = row.original.iban;
          const bic = row.original.bic;
          return (
            <Typography variant="body2">
              {iban} <br />
              <small>{bic}</small>
            </Typography>
          );
        },
      },
      { accessorKey: "account_holder", header: "Kontoinhaber", size: 180 },
      { accessorKey: "bank_name", header: "Bank", size: 180 },
      {
        accessorKey: "review_state",
        header: "Prüfstatus",
        size: 170,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              label={row.original.review_state_label ?? "—"}
              color={REVIEW_STATE_COLOR[row.original.review_state] ?? "default"}
            />
            {row.original.is_customer_default && (
              <Tooltip title="Standardkonto des Kunden">
                <StarIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "objekte_count",
        header: "Objekte",
        size: 100,
        Cell: ({ row }) =>
          Array.isArray(row.original.objekte) ? row.original.objekte.length : 0,
      },
      ...(scope === "admin"
        ? [
            {
              // accessorKey ist bewusst kunde.name1 (nicht display_name), damit
              // der Filter-Query-Parameter zum bestehenden Backend-Filter
              // AllowedFilter::partial('kunde.name1') passt. id muss explizit
              // gesetzt werden, da @tanstack/table-core Punkte im accessorKey
              // sonst automatisch zu Unterstrichen wandelt (id würde sonst
              // "kunde_name1" statt "kunde.name1" lauten und der
              // Filter-Parameter ginge verloren).
              accessorKey: "kunde.name1",
              id: "kunde.name1",
              header: "Kunde",
              size: 200,
              Cell: ({ row }) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {row.original.kunde_mismatch && (
                    <Tooltip title="Mindestens ein verknüpftes Objekt gehört laut Vertrag zu einem anderen Kunden">
                      <WarningAmberIcon
                        color="warning"
                        fontSize="small"
                        aria-label="Kunde-Verknüpfung stimmt nicht mit Objekt überein"
                      />
                    </Tooltip>
                  )}
                  <Typography variant="body2" noWrap>
                    {row.original.kunde?.display_name ?? "—"}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Kunde ändern"
                    onClick={() => setKundeModalBankAccount(row.original)}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ),
            },
          ]
        : []),
      {
        id: "actions",
        header: "",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          // Gate on the flags the backend computed — never re-derive from roles.
          const account = row.original;
          const has_actions =
            account.can_review ||
            account.can_manage_assignments ||
            account.can_update;

          if (!has_actions) return null;

          return (
            <IconButton
              size="small"
              aria-label="Aktionen"
              onClick={(event) => handleMenuOpen(event, account)}
            >
              <MoreVert />
            </IconButton>
          );
        },
      },
    ],
    [scope]
  );

  // Built on every render rather than memoized: the list is at most five items,
  // and memoizing on [menuRow] alone would capture stale action callbacks.
  const buildMenuItems = () => {
    if (!menuRow) return [];

    const items = [];

    if (menuRow.can_review && menuRow.review_state !== "claims_relevant") {
      items.push(
        <MenuItem
          key="approve"
          onClick={() =>
            runAction(() => setReviewState(menuRow.id, "claims_relevant"))
          }
        >
          <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
          Für Schadenzahlungen freigeben
        </MenuItem>
      );
    }

    if (menuRow.can_review && menuRow.review_state !== "rejected") {
      items.push(
        <MenuItem
          key="reject"
          onClick={() =>
            runAction(() => setReviewState(menuRow.id, "rejected"))
          }
        >
          <BlockIcon fontSize="small" sx={{ mr: 1 }} />
          Nicht relevant für Schäden
        </MenuItem>
      );
    }

    if (menuRow.can_manage_assignments) {
      // Only an account that may actually be paid from can be the default.
      if (menuRow.is_offerable_for_claims && !menuRow.is_customer_default) {
        items.push(
          <MenuItem
            key="set-default"
            onClick={() => {
              handleMenuClose();
              setDefaultDialogAccount(menuRow);
            }}
          >
            <StarIcon fontSize="small" sx={{ mr: 1 }} />
            Als Standardkonto setzen
          </MenuItem>
        );
      }

      if (menuRow.is_customer_default) {
        items.push(
          <MenuItem
            key="unset-default"
            onClick={() =>
              runAction(() => setCustomerDefault(menuRow.id, false))
            }
          >
            <StarBorderIcon fontSize="small" sx={{ mr: 1 }} />
            Standardkonto entfernen
          </MenuItem>
        );
      }

      items.push(
        <MenuItem
          key="assign-objects"
          onClick={() => {
            const account = menuRow;
            handleMenuClose();
            setChoiceDialogAccount(account);
          }}
        >
          <MapsHomeWorkOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Objekte zuweisen
        </MenuItem>
      );
    }

    if (menuRow.can_update) {
      items.push(
        <MenuItem
          key="edit"
          onClick={() => {
            const account = menuRow;
            handleMenuClose();
            setEditDialogAccount(account);
          }}
        >
          <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Kontodaten ändern
        </MenuItem>
      );
    }

    return items;
  };

  const menuItems = buildMenuItems();

  // Pagination-Handler
  const handlePaginationChange = (pagination) => {
    const next =
      typeof pagination === "function"
        ? pagination({ pageIndex, pageSize })
        : pagination;

    if (!next) return;

    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  const filterTabs = useMemo(
    () =>
      [
        { key: "all", label: "Alle" },
        { key: "pending", label: "Prüfung offen" },
        { key: "claims_relevant", label: "Freigegeben" },
        { key: "rejected", label: "Nicht relevant" },
      ].map((tab) => ({
        ...tab,
        count: reviewStateCounts?.[tab.key],
        onClick: handleTabChange,
      })),
    [reviewStateCounts]
  );

  return (
    <>
      {canCreateAccount && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Neues Bankkonto
          </Button>
        </Box>
      )}

      <DataTable
        testId="customerBankAccounts-table"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        hasLoaded={hasLoaded}
        title="Bankkonten"
        enableRowExpansion
        renderRowExpansionContent={(row) => row._expand}
        enablePagination
        manualPagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRows={totalRows}
        onChangePage={handlePaginationChange}
        onChangeSorting={(sorting) =>
          updateTableState(tableKey, {
            sorting: applySortingWithAccessorKeys(sorting, columns),
          })
        }
        onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey={
          scope === "admin"
            ? "v2_table_bank_accounts"
            : "v2_table_customer_bank_accounts"
        }
        filterTabs={filterTabs}
        activeFilterTab={activeReviewState}
        renderDetailPanel={({ row }) => (
          <Box sx={{ p: 2 }}>
            {Array.isArray(row.original.objekte) &&
            row.original.objekte.length > 0 ? (
              <DataTable
                columns={[
                  {
                    accessorKey: "bezeichnung",
                    header: "Bezeichnung",
                    size: 200,
                  },
                  ...(row.original.kunde?.object_ref_num_label
                    ? [
                        {
                          accessorKey: "customer_reference_number",
                          header: row.original.kunde.object_ref_num_label,
                          size: 100,
                        },
                      ]
                    : []),
                  { accessorKey: "strasse", header: "Straße", size: 150 },
                  { accessorKey: "hausnr", header: "Hausnummer", size: 100 },
                  { accessorKey: "plz", header: "PLZ", size: 80 },
                  { accessorKey: "ort", header: "Ort", size: 150 },
                  {
                    accessorKey: "vertrag",
                    header: "Vertrag",
                    size: 140,
                    Cell: ({ row: objektRow }) =>
                      objektRow.original.vertrag?.vertragsnummer || "–",
                  },
                  {
                    id: "unlink",
                    header: "",
                    size: 60,
                    enableSorting: false,
                    Cell: ({ row: objekt_row }) =>
                      row.original.can_manage_assignments && (
                        <Tooltip title="Verknüpfung entfernen">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setUnlinkTarget({
                                bankAccount: row.original,
                                objekt: objekt_row.original,
                              })
                            }
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                  },
                ]}
                data={row.original.objekte}
                pageSize={99999}
                enableFooter={false}
                enableTopToolbar={false}
                enablePagination={false}
                enableColumnFilters={false}
                enableSorting={true}
                enableBottomToolbar={false}
                enableGlobalFilter={true}
                initialState={{
                  globalFilter: tableState.searchword,
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ pl: 2 }}>
                Keine Objekte verknüpft
              </Typography>
            )}
          </Box>
        )}
      />

      {scope === "admin" && (
        <BankAccountKundeModal
          open={!!kundeModalBankAccount}
          bankAccount={kundeModalBankAccount}
          onClose={() => setKundeModalBankAccount(null)}
          onSuccess={() => fetchData(tableKey)}
        />
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor) && menuItems.length > 0}
        onClose={handleMenuClose}
      >
        {menuItems}
      </Menu>

      {choiceDialogAccount && (
        <AssignObjectsChoiceDialog
          open
          onClose={() => setChoiceDialogAccount(null)}
          onContinue={handleAssignChoiceContinue}
        />
      )}

      {bulkDialogAccount && (
        <BulkAssignObjectsDialog
          open
          bankAccount={bulkDialogAccount}
          onClose={() => setBulkDialogAccount(null)}
          onAssigned={refresh}
        />
      )}

      {importDialogAccount && (
        <BankAccountObjectImportDialog
          open
          bankAccount={importDialogAccount}
          onClose={() => setImportDialogAccount(null)}
          onSuccess={refresh}
        />
      )}

      {editDialogAccount && (
        <EditBankAccountDialog
          open
          bankAccount={editDialogAccount}
          onClose={() => setEditDialogAccount(null)}
          onSuccess={refresh}
        />
      )}

      <SetDefaultAccountDialog
        open={!!defaultDialogAccount}
        bankAccount={defaultDialogAccount}
        onClose={() => setDefaultDialogAccount(null)}
        onConfirmed={refresh}
      />

      {addDialogOpen && (
        <AddBankAccountDialog
          open
          onClose={() => setAddDialogOpen(false)}
          onCreated={refresh}
        />
      )}

      <Dialog open={!!unlinkTarget} onClose={() => setUnlinkTarget(null)}>
        <DialogTitle>Verknüpfung entfernen</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Soll die Verknüpfung von{" "}
            <strong>{unlinkTarget?.objekt?.bezeichnung}</strong> mit dem Konto{" "}
            <strong>{unlinkTarget?.bankAccount?.iban}</strong> entfernt werden?
            Schadenzahlungen für dieses Objekt gehen danach nicht mehr
            automatisch an dieses Konto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={() => setUnlinkTarget(null)}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const target = unlinkTarget;
              setUnlinkTarget(null);
              runAction(() => detachObject(target.bankAccount.id, target.objekt.id));
            }}
          >
            Entfernen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

BankAccountTable.propTypes = {
  scope: PropTypes.oneOf(["admin", "customer"]),
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
